'use client'

import { createContext, useContext, useEffect, useState } from 'react'
import { PlayCircle, Loader2 } from 'lucide-react'
import type { User as FirebaseUser } from 'firebase/auth'
import { CoinIcon } from '@/components/coin-icon'
import { adsAvailable, fetchAdStatus, setAdUserId, warmUpAd, watchAdForCoins, type AdStatus, type AdFormat } from '@/lib/admob'

// "Watch ad, get coins" - one reusable card used in the Wallet and inside the Boost sheet.
// The coins are credited by the SERVER (/api/rewards ad_start -> ad_claim); the app only shows the ad.
export function WatchAdCard({
  user, onEarned, compact = false, needCoins = 0, format = 'rewarded',
}: {
  user?: FirebaseUser | null
  onEarned?: (coins: number) => void // lets the page bump its local coin balance right away
  compact?: boolean
  needCoins?: number // when set, shows "You need N more coins" above the button
  format?: AdFormat // 'interstitial' = the Rewarded interstitial unit (used in the Boost sheet), falls back to rewarded video
}) {
  const [status, setStatus] = useState<AdStatus | null>(null)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  const [cool, setCool] = useState(0)
  const canShow = adsAvailable()

  // Start loading the ad as soon as the card appears, so the tap shows it right away.
  useEffect(() => { if (user && canShow) { setAdUserId(user.uid); warmUpAd(format); if (format !== 'rewarded') warmUpAd('rewarded') } }, [user, canShow, format])

  useEffect(() => {
    if (!user) return
    let off = false
    void (async () => {
      try { const s = await fetchAdStatus(await user.getIdToken()); if (!off) { setStatus(s); setCool(Math.ceil(s.cooldownLeftMs / 1000)) } } catch { /* card stays quiet */ }
    })()
    return () => { off = true }
  }, [user])

  useEffect(() => {
    if (cool <= 0) return
    const t = setTimeout(() => setCool((c) => Math.max(0, c - 1)), 1000)
    return () => clearTimeout(t)
  }, [cool])

  const watch = async () => {
    if (!user || busy) return
    setBusy(true); setMsg('')
    try {
      const r = await watchAdForCoins(await user.getIdToken(), format)
      setStatus(r.status); setCool(Math.ceil(r.status.cooldownLeftMs / 1000))
      if (r.granted > 0) { onEarned?.(r.granted); setMsg(`+${r.granted} coins added`) }
      else if (r.reason === 'capped') setMsg("Today's boxes are done. Come back tomorrow!")
      else if (r.reason === 'rowWait') setMsg('All boxes of this round are open. New boxes soon!')
      else if (r.reason === 'cooldown') setMsg('Next ad is almost ready.')
      else setMsg('Watch the full ad to earn coins.')
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Ad could not load. Try again.')
    } finally { setBusy(false) }
  }

  // Before the status arrives (or if it failed to load) the card must NOT claim "come back tomorrow": the server enforces the real limit on tap.
  const left = status ? status.remaining : 1
  const per = status?.nextReward ?? status?.coinsPerAd ?? 10
  const full = !!status?.rowFull
  const disabled = busy || !canShow || left <= 0 || cool > 0 || full

  return (
    <section style={{
      borderRadius: 16, padding: compact ? '.75rem .85rem' : '1rem', marginBottom: compact ? '1rem' : 0,
      border: '1px solid var(--line, rgba(255,255,255,.12))',
      background: 'linear-gradient(135deg, rgba(245,184,0,.14), rgba(245,184,0,.04))',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <span style={{ width: 38, height: 38, borderRadius: 12, display: 'grid', placeItems: 'center', background: 'rgba(245,184,0,.2)', flexShrink: 0 }}>
          <PlayCircle size={20} color="#f5b800" />
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <strong style={{ display: 'block', fontSize: '.9rem' }}>Open a gift box</strong>
          <small style={{ opacity: .75, fontSize: '.72rem' }}>
            {status ? `${status.remaining} of ${status.cap} boxes left today` : 'Earn free coins'} · next box {per} <CoinIcon size={12} />
          </small>
        </div>
      </div>
      {needCoins > 0 && <p style={{ margin: '0.5rem 0 0', fontSize: '.74rem', color: '#f5b800', fontWeight: 700 }}>You need {needCoins} more coins.</p>}
      <button
        type="button" className="primary-button" disabled={disabled} onClick={watch}
        style={{ width: '100%', minHeight: 42, marginTop: '0.75rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
      >
        {busy ? <><Loader2 size={16} className="spin-icon" /> Loading ad…</>
          : !canShow ? 'Watch ads in the Animora app'
          : left <= 0 ? 'Come back tomorrow'
          : full ? 'New boxes in a while'
          : cool > 0 ? `Next box in ${cool}s`
          : <>Watch ad · open box · +{per} coins</>}
      </button>
      {msg && <p style={{ margin: '0.5rem 0 0', fontSize: '.74rem', textAlign: 'center' }}>{msg}</p>}
    </section>
  )
}


// ---------------------------------------------------------------------------
// Slim, optional "Watch ad" line for purchase sheets. It shows ONLY when the person is short of coins,
// ONLY inside the Android app (where AdMob exists), never plays by itself and disappears once the
// daily ads are used up. The page provides the signed-in user + a way to bump the local balance.
// ---------------------------------------------------------------------------
export const CoinAdContext = createContext<{ user?: FirebaseUser | null; onEarned?: (coins: number) => void } | null>(null)

export function CoinShortAd({ needCoins = 0 }: { needCoins?: number }) {
  const ctx = useContext(CoinAdContext)
  const user = ctx?.user
  const [status, setStatus] = useState<AdStatus | null>(null)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  const [cool, setCool] = useState(0)
  const canShow = adsAvailable()

  useEffect(() => { if (user && canShow) { setAdUserId(user.uid); warmUpAd() } }, [user, canShow])

  useEffect(() => {
    if (!user || !canShow) return
    let off = false
    void (async () => {
      try { const s = await fetchAdStatus(await user.getIdToken()); if (!off) { setStatus(s); setCool(Math.ceil(s.cooldownLeftMs / 1000)) } } catch { /* stay quiet */ }
    })()
    return () => { off = true }
  }, [user, canShow])

  useEffect(() => {
    if (cool <= 0) return
    const t = setTimeout(() => setCool((c) => Math.max(0, c - 1)), 1000)
    return () => clearTimeout(t)
  }, [cool])

  // Nothing to show on the website, before the status is known, or when today's ads are finished.
  if (!ctx || !user || !canShow || !status || (status.remaining <= 0 && !msg)) return null

  const watch = async () => {
    if (busy || cool > 0) return
    setBusy(true); setMsg('')
    try {
      const r = await watchAdForCoins(await user.getIdToken())
      setStatus(r.status); setCool(Math.ceil(r.status.cooldownLeftMs / 1000))
      if (r.granted > 0) { ctx.onEarned?.(r.granted); setMsg(`+${r.granted} coins added`) }
      else if (r.reason === 'skipped') setMsg('Watch the full ad to get coins.')
      else if (r.reason === 'rowWait') setMsg('All boxes of this round are open. Try again later.')
    } catch { setMsg('Ad is not ready right now. Try again in a bit.') }
    finally { setBusy(false) }
  }

  return (
    <div style={{ marginTop: '0.5rem', textAlign: 'center' }}>
      <button
        type="button" onClick={watch} disabled={busy || cool > 0 || status.remaining <= 0 || !!status.rowFull}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8, padding: '0.5rem 1rem', borderRadius: 999,
          fontSize: '.76rem', fontWeight: 700, color: 'var(--ink)', background: 'rgba(245,184,0,.16)',
          border: '1px solid rgba(245,184,0,.45)', opacity: busy || cool > 0 ? 0.6 : 1,
        }}
      >
        <PlayCircle size={16} color="#f5b800" />
        {busy ? 'Loading ad…' : status.rowFull ? 'New boxes in a while' : cool > 0 ? `Next ad in ${cool}s` : `Watch a short ad · +${status.nextReward ?? status.coinsPerAd} coins`}
      </button>
      {msg && <div style={{ marginTop: 4, fontSize: '.72rem', color: 'var(--muted)' }}>{msg}</div>}
      {!msg && needCoins > 0 && <div style={{ marginTop: 4, fontSize: '.68rem', color: 'var(--muted)' }}>Optional - totally up to you</div>}
    </div>
  )
}


// ---------------------------------------------------------------------------
// Slim "Watch ad, get coins" button for the Chess / Ludo result card. 100% optional: shown ONLY inside the Android app,
// only while today's ads are not used up. Coins are granted by the SERVER (same ad_start -> ad_claim flow, same daily cap and
// cooldown as the Wallet card), so nothing here can be abused. Test mode follows ADMOB_FORCE_TEST like every other ad.
// ---------------------------------------------------------------------------
export function ResultAdButton({ user }: { user?: FirebaseUser | null }) {
  const [status, setStatus] = useState<AdStatus | null>(null)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  const [cool, setCool] = useState(0)
  const canShow = adsAvailable()

  useEffect(() => { if (user && canShow) { setAdUserId(user.uid); warmUpAd() } }, [user, canShow])
  useEffect(() => {
    if (!user || !canShow) return
    let off = false
    void (async () => {
      try { const s = await fetchAdStatus(await user.getIdToken()); if (!off) { setStatus(s); setCool(Math.ceil(s.cooldownLeftMs / 1000)) } } catch { /* stay quiet */ }
    })()
    return () => { off = true }
  }, [user, canShow])
  useEffect(() => {
    if (cool <= 0) return
    const t = setTimeout(() => setCool((c) => Math.max(0, c - 1)), 1000)
    return () => clearTimeout(t)
  }, [cool])

  if (!user || !canShow || !status || (status.remaining <= 0 && !msg)) return null

  const watch = async () => {
    if (busy || cool > 0) return
    setBusy(true); setMsg('')
    try {
      const r = await watchAdForCoins(await user.getIdToken())
      setStatus(r.status); setCool(Math.ceil(r.status.cooldownLeftMs / 1000))
      if (r.granted > 0) setMsg(`+${r.granted} coins added`)
      else if (r.reason === 'skipped') setMsg('Watch the full ad to get coins.')
      else if (r.reason === 'cooldown') setMsg('Next ad is almost ready.')
      else if (r.reason === 'rowWait') setMsg('All boxes of this round are open.')
    } catch { setMsg('Ad is not ready right now. Try again in a bit.') }
    finally { setBusy(false) }
  }

  return (
    <div className="lu-adbtn-wrap">
      <button type="button" className="lu-adbtn" onClick={watch} disabled={busy || cool > 0 || status.remaining <= 0 || !!status.rowFull}>
        <PlayCircle size={16} />
        <span>{busy ? 'Loading ad…' : cool > 0 ? `Next ad in ${cool}s` : `Watch a short ad · +${status.nextReward ?? status.coinsPerAd}`}</span>
        {!busy && cool <= 0 && <CoinIcon size={13} />}
      </button>
      {msg && <small>{msg}</small>}
    </div>
  )
}
